import { useState, useEffect, useCallback } from "react";

const DB_NAME = "park-row-offline";
const DB_VERSION = 1;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Hook for managing offline data caching with IndexedDB
 */
export function useOfflineCache<T>(storeName: string) {
  const [db, setDb] = useState<IDBDatabase | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Initialize IndexedDB
  useEffect(() => {
    if (!("indexedDB" in window)) {
      console.warn("IndexedDB not supported");
      setIsReady(true);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("Failed to open IndexedDB:", request.error);
      setIsReady(true);
    };

    request.onsuccess = () => {
      setDb(request.result);
      setIsReady(true);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      
      // Create stores for different data types
      if (!database.objectStoreNames.contains("gigs")) {
        database.createObjectStore("gigs", { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains("products")) {
        database.createObjectStore("products", { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains("meta")) {
        database.createObjectStore("meta", { keyPath: "key" });
      }
    };

    return () => {
      if (db) {
        db.close();
      }
    };
  }, []);

  // Save data to cache
  const cacheData = useCallback(
    async (data: T[]): Promise<boolean> => {
      if (!db || !db.objectStoreNames.contains(storeName)) return false;

      return new Promise((resolve) => {
        try {
          const transaction = db.transaction([storeName, "meta"], "readwrite");
          const store = transaction.objectStore(storeName);
          const metaStore = transaction.objectStore("meta");

          // Clear existing data
          store.clear();

          // Add new data
          data.forEach((item) => {
            store.add(item);
          });

          // Update timestamp
          metaStore.put({
            key: `${storeName}_timestamp`,
            value: Date.now(),
          });

          transaction.oncomplete = () => resolve(true);
          transaction.onerror = () => {
            console.error("Cache transaction failed:", transaction.error);
            resolve(false);
          };
        } catch (e) {
          console.error("Failed to cache data:", e);
          resolve(false);
        }
      });
    },
    [db, storeName]
  );

  // Get data from cache
  const getCachedData = useCallback(async (): Promise<T[] | null> => {
    if (!db || !db.objectStoreNames.contains(storeName)) return null;

    return new Promise((resolve) => {
      try {
        const transaction = db.transaction([storeName, "meta"], "readonly");
        const store = transaction.objectStore(storeName);
        const metaStore = transaction.objectStore("meta");

        // Check if cache is expired
        const metaRequest = metaStore.get(`${storeName}_timestamp`);
        
        metaRequest.onsuccess = () => {
          const timestamp = metaRequest.result?.value;
          if (!timestamp || Date.now() - timestamp > CACHE_EXPIRY) {
            resolve(null);
            return;
          }

          // Get all cached data
          const dataRequest = store.getAll();
          dataRequest.onsuccess = () => {
            resolve(dataRequest.result || null);
          };
          dataRequest.onerror = () => resolve(null);
        };

        metaRequest.onerror = () => resolve(null);
      } catch (e) {
        console.error("Failed to get cached data:", e);
        resolve(null);
      }
    });
  }, [db, storeName]);

  // Check if cache is valid
  const isCacheValid = useCallback(async (): Promise<boolean> => {
    if (!db || !db.objectStoreNames.contains("meta")) return false;

    return new Promise((resolve) => {
      try {
        const transaction = db.transaction("meta", "readonly");
        const store = transaction.objectStore("meta");
        const request = store.get(`${storeName}_timestamp`);

        request.onsuccess = () => {
          const timestamp = request.result?.value;
          resolve(!!timestamp && Date.now() - timestamp < CACHE_EXPIRY);
        };
        request.onerror = () => resolve(false);
      } catch {
        resolve(false);
      }
    });
  }, [db, storeName]);

  // Clear cache
  const clearCache = useCallback(async (): Promise<void> => {
    if (!db || !db.objectStoreNames.contains(storeName)) return;

    return new Promise((resolve) => {
      try {
        const transaction = db.transaction([storeName, "meta"], "readwrite");
        transaction.objectStore(storeName).clear();
        transaction.objectStore("meta").delete(`${storeName}_timestamp`);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }, [db, storeName]);

  return {
    isReady,
    cacheData,
    getCachedData,
    isCacheValid,
    clearCache,
  };
}

/**
 * Combined hook for managing offline gigs and products
 */
export function useOfflineData() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const gigsCache = useOfflineCache<any>("gigs");
  const productsCache = useOfflineCache<any>("products");

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return {
    isOnline,
    gigsCache,
    productsCache,
  };
}
